/**
 * onestop-inspector — the per-block config forms.
 *
 * HYBRID, BY RULING (2026-09-01): a generic renderer over a hand-maintained field
 * table for the twelve simple types, and a purpose-built panel for the eight that
 * earn one — `search`, `identity-panel`, `help-contact`, `faq`, `stats`, and the
 * three scope-bearing live blocks (`queues`, `appointments`, `events`).
 *
 * WHY THE TABLE IS HAND-MAINTAINED AND NOT REFLECTED
 * The authority is `ONE_STOP_BLOCK_PROPS_SCHEMAS` — runtime zod. Zod carries no
 * label, no help text and no ordering, and its internal shape is not a public API,
 * so walking it to build a form would couple this file to zod's internals and
 * still leave every label to be written by hand. The table below is therefore the
 * SECOND spelling of each block's fields, and that is a real cost: a registry key
 * renamed upstream goes stale here silently. What limits the damage is that a
 * stale key writes config the publish validator rejects by path, which lands on
 * the block via `publishFaultsByBlock` rather than disappearing.
 *
 * WHAT IS DELIBERATELY NOT OFFERED
 * `block.data` — the resolver produces it. `nav` — derived from every published
 * page's `navLabel`. Anything in the "not authored" table of the kit's §4.2. A
 * field here for one of those would be a control that looks like it works.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, JSX } from "react";
import { getIntentRegistry } from "@v0y4ge/api-client";
import { Dropdown } from "@v0y4ge/jrni-ui";
import { osLabel } from "./onestop-canvas";
import type { OneStopBlockInstance } from "./onestop-page-api";

/* ── Library rows the scope pickers select from ─────────────────────────
 *  `undefined` and `[]` are different facts and the picker says which: not
 *  loaded on this screen, versus the tenant genuinely has none. Conflating them
 *  is the same mistake the renderer's empty-state copy exists to avoid.
 */
export interface LibRow { id: string; name: string; meta?: string }
export interface OneStopLibs {
  queues?: LibRow[];
  services?: LibRow[];
  events?: LibRow[];
  categories?: LibRow[];
  locations?: LibRow[];
  orgNodes?: LibRow[];
  staff?: LibRow[];
  /** Where an author goes to create one, per source. */
  createRoute?: Partial<Record<LibSource, { label: string; go: () => void }>>;
}
export type LibSource = "queues" | "services" | "events" | "categories" | "locations" | "orgNodes" | "staff";

/* ── Field descriptors ─────────────────────────────────────────────────── */

type Field =
  | { k: "group"; label: string }
  | { k: "locale"; path: string; label: string; hint?: string; lines?: number }
  | { k: "text"; path: string; label: string; hint?: string }
  | { k: "bool"; path: string; label: string; hint?: string }
  | { k: "int"; path: string; label: string; hint?: string }
  | { k: "enum"; path: string; label: string; options: string[]; hint?: string }
  | { k: "date"; path: string; label: string; hint?: string }
  | { k: "ids"; path: string; label: string; source: LibSource; hint?: string };

/**
 * The twelve generic types. Order is the order an author meets the block's own
 * decisions: what it points at, then how it presents, then the trimmings.
 */
const FIELDS: Record<string, Field[]> = {
  "context-banner": [
    { k: "locale", path: "text", label: "Banner text", lines: 2, hint: "Required. The one thing students should know right now." },
    { k: "enum", path: "tone", label: "Tone", options: ["info", "highlight"], hint: "Meaning, not colour." },
    { k: "date", path: "activeFrom", label: "Runs from", hint: "Required. A civil date in this page's zone." },
    { k: "date", path: "activeTo", label: "Runs to", hint: "Required." },
  ],
  directory: [
    { k: "ids", path: "scope.ids", label: "Specific nodes", source: "orgNodes" },
    { k: "int", path: "depth", label: "Levels deep" },
    { k: "enum", path: "sort", label: "Sort", options: ["name", "curated"] },
    { k: "bool", path: "collapsedByDefault", label: "Start collapsed" },
    { k: "bool", path: "showCapabilities", label: "Show what each can do" },
  ],
  checklist: [
    { k: "locale", path: "title", label: "Title", hint: "{name} is replaced with the student's first name." },
    { k: "int", path: "maxItems", label: "Show at most" },
    { k: "enum", path: "orderPolicy", label: "Order by", options: ["severity", "recency"] },
    { k: "locale", path: "emptyText", label: "When there is nothing outstanding" },
  ],
  "my-visits": [
    { k: "locale", path: "title", label: "Title" },
    { k: "locale", path: "subtitle", label: "Subtitle" },
  ],
  notice: [
    { k: "locale", path: "text", label: "Text", lines: 3, hint: "Required." },
    { k: "enum", path: "tone", label: "Tone", options: ["info", "warning", "success"] },
    { k: "bool", path: "dismissible", label: "Student can dismiss it", hint: "Dismissal is per viewer and never stored on the page." },
    { k: "bool", path: "showIcon", label: "Show the icon" },
  ],
  button: [
    { k: "locale", path: "label", label: "Label", hint: "Required." },
    { k: "group", label: "Where it goes" },
    { k: "enum", path: "target.kind", label: "Target", options: ["url", "deep-link"] },
    { k: "text", path: "target.url", label: "URL", hint: "For an external target." },
    { k: "text", path: "target.route", label: "In-platform path", hint: 'Must begin with a single "/".' },
    { k: "enum", path: "variant", label: "Weight", options: ["primary", "secondary"] },
    { k: "bool", path: "newTab", label: "Open in a new tab", hint: "External targets only." },
  ],
  quote: [
    { k: "locale", path: "text", label: "Quote", lines: 3 },
    { k: "locale", path: "attribution", label: "Attribution" },
  ],
  social: [{ k: "group", label: "Accounts" }],
  spacer: [{ k: "enum", path: "size", label: "Height", options: ["small", "medium", "large"] }],
  "join-queue": [
    { k: "ids", path: "queueId", label: "Queue", source: "queues", hint: "Required — the desk this card joins." },
    { k: "locale", path: "label", label: "Button label" },
    { k: "enum", path: "presentation", label: "Presentation", options: ["inline", "navigate"] },
    { k: "bool", path: "showLiveWait", label: "Show the live wait" },
  ],
  "register-event": [
    { k: "ids", path: "eventId", label: "Event", source: "events", hint: "Required — the event this card registers for." },
    { k: "locale", path: "label", label: "Button label" },
    { k: "enum", path: "presentation", label: "Presentation", options: ["inline", "navigate"] },
    { k: "bool", path: "showCapacity", label: "Show remaining capacity" },
  ],
  "book-appointment": [
    { k: "ids", path: "preselects.serviceId", label: "Service", source: "services", hint: "Preselected — a preselected step is a skipped step." },
    { k: "ids", path: "preselects.locationId", label: "Location", source: "locations" },
    { k: "ids", path: "preselects.staffId", label: "Specialist", source: "staff" },
    { k: "locale", path: "label", label: "Button label" },
    { k: "locale", path: "description", label: "Description" },
    { k: "enum", path: "presentation", label: "Presentation", options: ["inline", "navigate"] },
  ],
};

/** Which types get a purpose-built panel instead. */
export const HAND_AUTHORED = new Set(["search", "identity-panel", "help-contact", "faq", "stats", "queues", "appointments", "events"]);

/* ── Path helpers ──────────────────────────────────────────────────────
 *  Config is arbitrary JSON by contract (`z.record(z.string(), z.unknown())` on
 *  the read path), so every access defaults rather than asserts. A block whose
 *  config predates a field, or carries a shape this build has never seen, must
 *  render as an empty control and not throw.
 */
function readPath(config: unknown, path: string): unknown {
  let cursor: any = config;
  for (const key of path.split(".")) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = cursor[key];
  }
  return cursor;
}

function writePath(config: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const keys = path.split(".");
  const next: Record<string, unknown> = { ...config };
  let cursor: any = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i]!;
    const existing = cursor[key];
    cursor[key] = existing && typeof existing === "object" && !Array.isArray(existing) ? { ...existing } : {};
    cursor = cursor[key];
  }
  const last = keys[keys.length - 1]!;
  /* An empty value is REMOVED, not stored as "". A locale map with an empty
     string fails `PortalRequiredLocaleStringSchema`, which demands at least one
     non-empty entry — so writing "" would turn a merely unfinished field into a
     publish refusal that reads as a contract error. */
  if (value === undefined || value === "" || value === null) delete cursor[last];
  else cursor[last] = value;
  return next;
}

/* ── Controls ──────────────────────────────────────────────────────────── */

const SORT_LABEL = "Sort";
const PRESENTATION_LABEL = "Presentation";
const VISIBILITY_LABEL = "Who sees it";
const LABEL: CSSProperties = { fontSize: 11, fontWeight: 700, color: "#333333", marginBottom: 4, display: "block" };
const HINT: CSSProperties = { fontSize: 10.5, color: "#8A92A0", lineHeight: 1.45, marginTop: 3 };
const INPUT: CSSProperties = { width: "100%", boxSizing: "border-box", height: 30, padding: "0 8px", border: "1px solid #D1D4DC", borderRadius: 6, fontSize: 12, fontFamily: "inherit", color: "#333333", background: "#FFFFFF" };
const ROW: CSSProperties = { marginBottom: 12 };
const MONO: CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
const GROUP: CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8A92A0", margin: "16px 0 8px" };
const WARN: CSSProperties = { fontSize: 10.5, lineHeight: 1.5, color: "#8A5A08", background: "#FFFBF2", border: "1px solid #F0DCB8", borderRadius: 6, padding: "6px 8px", marginTop: 4 };
const CHIP: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 6px 3px 8px", borderRadius: 999, background: "#F1EFFC", color: "#3D31A0" };

/** Text that commits on blur, per the save ruling, while staying responsive. */
function BlurText(props: { value: string; lines?: number; onCommit: (v: string) => void; placeholder?: string }): JSX.Element {
  const [draft, setDraft] = useState(props.value);
  const settled = useRef(props.value);
  /* Adopt an external change (a different block selected, a reload) without
     stamping on what the author is mid-way through typing. */
  useEffect(() => {
    if (props.value !== settled.current) { settled.current = props.value; setDraft(props.value); }
  }, [props.value]);
  const commit = () => { settled.current = draft; if (draft !== props.value) props.onCommit(draft); };
  if (props.lines && props.lines > 1) {
    return (
      <textarea
        value={draft} rows={props.lines} placeholder={props.placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        style={{ ...INPUT, height: "auto", padding: "6px 8px", resize: "vertical", lineHeight: 1.5 }}
      />
    );
  }
  return (
    <input
      value={draft} placeholder={props.placeholder}
      onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      style={INPUT}
    />
  );
}

/**
 * A scope picker: the ids it holds, and where to get more.
 *
 * Select-only in v1 (ruling): the picker lists what exists with a route out to
 * that object's own screen, and its EMPTY state links to where one is created
 * rather than being a dead end. Creating a queue from here would embed another
 * surface's creation flow, and its readiness rules, inside a config field.
 */
function IdPicker(props: {
  label: string; hint?: string; single?: boolean;
  value: string[]; rows?: LibRow[]; source: LibSource;
  createRoute?: { label: string; go: () => void };
  onChange: (ids: string[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rows = props.rows;
  const byId = useMemo(() => new Map((rows ?? []).map((r) => [r.id, r])), [rows]);
  /* A source this screen cannot offer is not shown at all when nothing is picked
     from it. The earlier version rendered a "not loaded here" warning, which read
     as a broken field rather than an absent one and put four of them in a row on
     the appointments block. When ids ARE saved against it, the chips still render
     — they are the author's data and hiding them would be worse. */
  if (rows === undefined && props.value.length === 0) return <></>;
  return (
    <div style={ROW}>
      <span style={LABEL}>{props.label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {props.value.length === 0 ? (
          <span style={{ fontSize: 11, color: "#8A92A0" }}>
            {/* An empty scope is NOT "nothing". Left empty, the resolver has no
                filter to apply, which is the opposite of what an author who
                skipped the field is likely to assume. */}
            Nothing picked — this block is unscoped, so it offers everything available.
          </span>
        ) : (
          props.value.map((id) => {
            const row = byId.get(id);
            return (
              <span key={id} style={row ? CHIP : { ...CHIP, background: "#FBEAE7", color: "#C0392B" }}>
                <span style={row ? undefined : MONO}>{row ? row.name : `missing · ${id}`}</span>
                <button
                  onClick={() => props.onChange(props.value.filter((x) => x !== id))}
                  aria-label={`Remove ${row?.name ?? id}`}
                  style={{ border: "none", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "0 3px" }}
                >
                  ×
                </button>
              </span>
            );
          })
        )}
      </div>
      {rows === undefined ? (
        <div style={HINT}>Saved on this block. The list to change it isn’t available on this screen.</div>
      ) : rows.length === 0 ? (
        <div style={WARN}>
          Your workspace has none yet.{" "}
          {props.createRoute ? (
            <button onClick={props.createRoute.go} style={{ border: "none", background: "transparent", padding: 0, color: "#8A5A08", fontWeight: 700, textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
              {props.createRoute.label}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            style={{ height: 28, padding: "0 10px", border: "1px dashed #B9A8F0", borderRadius: 6, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
          >
            {open ? "Done" : props.single ? "Choose…" : "Add…"}
          </button>
          {open ? (
            <div style={{ marginTop: 6, border: "1px solid #E6E8EC", borderRadius: 8, maxHeight: 190, overflow: "auto" }}>
              {rows.map((row) => {
                const on = props.value.includes(row.id);
                return (
                  <div
                    key={row.id}
                    onClick={() => {
                      if (props.single) { props.onChange(on ? [] : [row.id]); setOpen(false); return; }
                      props.onChange(on ? props.value.filter((x) => x !== row.id) : [...props.value, row.id]);
                    }}
                    style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "7px 9px", borderTop: "1px solid #F4F5F7", cursor: "pointer", background: on ? "#F6F5FF" : "#FFFFFF" }}
                  >
                    <span style={{ fontSize: 11, color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#C7CBD6", flex: "none", fontWeight: 800 }}>{on ? "✓" : "＋"}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 600 }}>{row.name}</span>
                      {row.meta ? <span style={{ display: "block", fontSize: 10.5, color: "#697180", marginTop: 1 }}>{row.meta}</span> : null}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      )}
      {props.hint ? <div style={HINT}>{props.hint}</div> : null}
      {props.value.length > 0 && rows ? (
        <div style={HINT}>{props.value.length} picked · resolves at read time, so edits to them show without republishing</div>
      ) : null}
    </div>
  );
}

/* ── Search chips: resolved as they are edited ──────────────────────────
 *  A chip fails two ways that look identical from the outside — a phrase in no
 *  intent, and an intent whose targets are empty — and both render as an empty
 *  result for the student. They get two different messages because they have two
 *  different fixes: change the wording, or give the intent targets.
 *
 *  Matching is WHOLE-PHRASE on the normalised form, exactly as the server does
 *  it: casefold, strip combining marks, drop long digit runs, strip
 *  non-alphanumerics, collapse whitespace. Written once, here, rather than
 *  imported — `normalizePortalQuery` is a contracts-internal today — and kept
 *  deliberately identical.
 */
function normalizeQuery(input: string): string {
  return input
    .normalize("NFKD")
    .toLowerCase()
    .replace(/\d{5,}/g, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

interface IntentEntry { phrases: string[]; targetCount: number }

function useIntentRegistry(): { entries: IntentEntry[] | null; failed: boolean } {
  const [entries, setEntries] = useState<IntentEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    getIntentRegistry()
      .then((res: any) => {
        if (!live) return;
        const intents = res?.data?.intents ?? res?.intents ?? [];
        setEntries(
          (Array.isArray(intents) ? intents : []).map((intent: any) => ({
            phrases: [...(intent?.phrases ?? []), ...(intent?.aliases ?? [])].map((p: any) => normalizeQuery(String(p))),
            targetCount: Object.values(intent?.targets ?? {}).flat().length,
          })),
        );
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);
  return { entries, failed };
}

type ChipVerdict = { state: "resolves"; count: number } | { state: "no-intent" } | { state: "no-targets" } | { state: "unknown" };

function resolveChip(text: string, entries: IntentEntry[] | null): ChipVerdict {
  if (!entries) return { state: "unknown" };
  const phrase = normalizeQuery(text);
  if (!phrase) return { state: "unknown" };
  const hit = entries.find((entry) => entry.phrases.includes(phrase));
  if (!hit) return { state: "no-intent" };
  if (hit.targetCount === 0) return { state: "no-targets" };
  return { state: "resolves", count: hit.targetCount };
}

function ChipEditor(props: { config: Record<string, unknown>; onPatch: (next: Record<string, unknown>) => void }): JSX.Element {
  const { entries, failed } = useIntentRegistry();
  const chips = (Array.isArray(props.config.chips) ? props.config.chips : []) as { label?: any; query?: string }[];
  const setChips = (next: unknown[]) => props.onPatch({ ...props.config, chips: next });
  const labelOf = (chip: { label?: any }) => {
    const value = chip.label;
    if (typeof value === "string") return value;
    if (value && typeof value === "object") return String(Object.values(value)[0] ?? "");
    return "";
  };
  return (
    <div style={ROW}>
      <span style={LABEL}>Chips</span>
      <div style={HINT}>
        Each chip is a search. A chip with no query searches its own label, so phrase it like something a student would type.
      </div>
      {failed ? <div style={WARN}>The intent registry didn’t load, so chips can’t be checked here. They still work; they just aren’t verified.</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
        {chips.map((chip, i) => {
          const text = chip.query || labelOf(chip);
          const verdict = resolveChip(text, entries);
          return (
            <div key={i} style={{ border: "1px solid #E6E8EC", borderRadius: 8, padding: 8 }}>
              <BlurText
                value={labelOf(chip)}
                placeholder="What a student would tap"
                onCommit={(v) => setChips(chips.map((c, j) => (j === i ? { ...c, label: v ? { "en-US": v } : undefined } : c)))}
              />
              {verdict.state === "resolves" ? (
                <div style={{ ...HINT, color: "#1E5C3F" }}>{verdict.count} result{verdict.count === 1 ? "" : "s"}</div>
              ) : verdict.state === "no-intent" ? (
                <div style={WARN}>Nothing matches “{text}”. A student tapping this gets an empty result — change the wording, or add the phrase to the intent registry.</div>
              ) : verdict.state === "no-targets" ? (
                <div style={WARN}>“{text}” matches an intent, but that intent has no targets — so it still resolves to nothing. Give the intent targets in the intent registry.</div>
              ) : null}
              <button
                onClick={() => setChips(chips.filter((_, j) => j !== i))}
                style={{ marginTop: 6, border: "none", background: "transparent", padding: 0, color: "#C0392B", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                Remove chip
              </button>
            </div>
          );
        })}
      </div>
      <button
        onClick={() => setChips([...chips, {}])}
        style={{ marginTop: 8, height: 28, padding: "0 10px", border: "1px dashed #B9A8F0", borderRadius: 6, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
      >
        Add a chip
      </button>
    </div>
  );
}

/* ── The inspector ─────────────────────────────────────────────────────── */

export interface OneStopInspectorProps {
  block: OneStopBlockInstance;
  locale: string;
  libs: OneStopLibs;
  /** Publish faults for THIS block, keyed by config path. */
  faults?: { path: string | null; message: string }[];
  onPatchConfig: (config: Record<string, unknown>) => void;
  onRename: (id: string) => void;
  onVisibility: (visibility: "always" | "signed-in" | "signed-out") => void;
  /** Ids already in the document, so a rename can refuse a duplicate. */
  takenIds: string[];
}

export function OneStopInspector(props: OneStopInspectorProps): JSX.Element {
  const { block, locale } = props;
  const config = (block.config ?? {}) as Record<string, unknown>;
  const patch = (path: string, value: unknown) => props.onPatchConfig(writePath(config, path, value));
  const faultFor = (path: string) => props.faults?.find((f) => f.path === path)?.message;

  const localeValue = (path: string): string => {
    const raw = readPath(config, path);
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      const map = raw as Record<string, unknown>;
      const own = map[locale];
      if (typeof own === "string") return own;
      const first = Object.values(map).find((v) => typeof v === "string");
      return typeof first === "string" ? first : "";
    }
    return "";
  };
  /* Writes ONLY this document's default locale key and leaves every other locale
     alone — a Spanish value somebody else authored survives an English edit. */
  const setLocaleValue = (path: string, text: string) => {
    const raw = readPath(config, path);
    const base = raw && typeof raw === "object" && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
    if (text) base[locale] = text;
    else delete base[locale];
    props.onPatchConfig(writePath(config, path, Object.keys(base).length ? base : undefined));
  };

  const idsAt = (path: string): string[] => {
    const raw = readPath(config, path);
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === "string" && raw) return [raw];
    return [];
  };

  const picker = (label: string, path: string, source: LibSource, opts?: { single?: boolean; hint?: string }) => (
    <IdPicker
      key={path}
      label={label} hint={opts?.hint} single={opts?.single}
      value={idsAt(path)} rows={props.libs[source]} source={source}
      createRoute={props.libs.createRoute?.[source]}
      onChange={(ids) => patch(path, opts?.single ? (ids[0] ?? undefined) : ids)}
    />
  );

  const generic = (FIELDS[block.type] ?? []).map((field, i) => {
    if (field.k === "group") return <div key={`g${i}`} style={GROUP}>{field.label}</div>;
    const fault = faultFor(field.path);
    const body =
      field.k === "locale" ? (
        <BlurText value={localeValue(field.path)} lines={field.lines} onCommit={(v) => setLocaleValue(field.path, v)} />
      ) : field.k === "text" || field.k === "date" ? (
        <BlurText value={String(readPath(config, field.path) ?? "")} placeholder={field.k === "date" ? "YYYY-MM-DD" : undefined} onCommit={(v) => patch(field.path, v)} />
      ) : field.k === "int" ? (
        <BlurText
          value={String(readPath(config, field.path) ?? "")}
          onCommit={(v) => {
            const n = parseInt(v, 10);
            patch(field.path, v === "" || isNaN(n) ? undefined : n);
          }}
        />
      ) : field.k === "bool" ? (
        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={readPath(config, field.path) !== false} onChange={(e) => patch(field.path, e.target.checked)} />
          <span>{field.label}</span>
        </label>
      ) : field.k === "enum" ? (
        <Dropdown<string> portal size="sm" aria-label={field.label} value={String(readPath(config, field.path) ?? "")}
          onChange={(v) => patch(field.path, v || undefined)}
          options={[{ value: "", label: "—" }, ...field.options.map((option) => ({ value: option, label: option }))]} />
      ) : (
        picker(field.label, field.path, field.source, { single: true, hint: field.hint })
      );

    if (field.k === "ids") return body;
    return (
      <div key={field.path} style={ROW}>
        {field.k === "bool" ? body : (<><span style={LABEL}>{field.label}</span>{body}</>)}
        {field.hint ? <div style={HINT}>{field.hint}</div> : null}
        {fault ? <div style={{ ...WARN, background: "#FBEAE7", borderColor: "#F3D0CB", color: "#C0392B" }}>{fault}</div> : null}
      </div>
    );
  });

  /* ── The eight hand-authored panels ────────────────────────────────── */
  const scopeTitles = (
    <>
      <div style={ROW}><span style={LABEL}>Section title</span><BlurText value={localeValue("title")} onCommit={(v) => setLocaleValue("title", v)} /><div style={HINT}>Left blank, the renderer uses its own localised heading — which is usually what you want on a page with several locales.</div></div>
      <div style={ROW}><span style={LABEL}>Subtitle</span><BlurText value={localeValue("subtitle")} onCommit={(v) => setLocaleValue("subtitle", v)} /></div>
    </>
  );

  const hand =
    block.type === "queues" ? (
      <>
        {scopeTitles}
        <div style={GROUP}>What it offers</div>
        {picker("Desks", "scope.queues", "queues", { hint: "Pick from your queue journeys. Leave empty and the page offers every desk." })}
        {picker("Locations", "scope.locations", "locations")}
        {picker("Org nodes", "scope.orgNodes", "orgNodes")}
        <div style={GROUP}>Presentation</div>
        <div style={ROW}><span style={LABEL}>Sort</span><Dropdown<string> portal size="sm" aria-label={SORT_LABEL} value={String(config.sort ?? "")} onChange={(v) => patch("sort", v || undefined)} options={[{ value: "", label: "shortest wait (default)" }, ...["shortest-wait", "name", "location", "curated"].map((o) => ({ value: o, label: o }))]} /></div>
        <div style={ROW}><span style={LABEL}>Show at most</span><BlurText value={String(config.maxItems ?? "")} onCommit={(v) => { const n = parseInt(v, 10); patch("maxItems", v === "" || isNaN(n) ? undefined : n); }} /><div style={HINT}>Blank shows every desk in scope.</div></div>
        {(["showWalkMinutes", "showOpenUntil", "showClosedDesks", "groupByLocation", "showOverflowLine"] as const).map((key) => (
          <label key={key} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer", marginBottom: 7 }}>
            <input type="checkbox" checked={config[key] !== false && !(key === "showClosedDesks" && config[key] === undefined)} onChange={(e) => patch(key, e.target.checked)} />
            <span>{key}</span>
          </label>
        ))}
      </>
    ) : block.type === "appointments" ? (
      <>
        {scopeTitles}
        <div style={GROUP}>What can be booked</div>
        {picker("Services", "scope.services", "services", { hint: "The page offers these. A snapshot — adding a service to a catalogue group later does not add it here." })}
        {picker("Categories", "scope.categories", "categories")}
        {picker("Locations", "scope.locations", "locations")}
        {picker("Specialists", "scope.staff", "staff")}
        <div style={GROUP}>Presentation</div>
        <div style={ROW}><span style={LABEL}>Show at most</span><BlurText value={String(config.maxItems ?? "")} onCommit={(v) => { const n = parseInt(v, 10); patch("maxItems", v === "" || isNaN(n) ? undefined : n); }} /></div>
        <div style={ROW}><span style={LABEL}>Book button</span><BlurText value={localeValue("ctaLabel")} onCommit={(v) => setLocaleValue("ctaLabel", v)} /></div>
        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer" }}><input type="checkbox" checked={config.showNextOpenings !== false} onChange={(e) => patch("showNextOpenings", e.target.checked)} /><span>Show the next openings</span></label>
      </>
    ) : block.type === "events" ? (
      <>
        {scopeTitles}
        <div style={GROUP}>What it lists</div>
        {picker("Events", "scope.events", "events", { hint: "Pick from your event journeys. Leave empty and the page lists them all." })}
        {picker("Categories", "scope.categories", "categories")}
        {picker("Org nodes", "scope.orgNodes", "orgNodes")}
        <div style={GROUP}>Presentation</div>
        <div style={ROW}><span style={LABEL}>Show at most</span><BlurText value={String(config.maxItems ?? "")} onCommit={(v) => { const n = parseInt(v, 10); patch("maxItems", v === "" || isNaN(n) ? undefined : n); }} /></div>
        <div style={ROW}><span style={LABEL}>Register button</span><BlurText value={localeValue("ctaLabel")} onCommit={(v) => setLocaleValue("ctaLabel", v)} /></div>
        {(["showRegistrationCounts", "showWaitlistState", "showAllEventsLink", "showFilterUi"] as const).map((key) => (
          <label key={key} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer", marginBottom: 7 }}>
            <input type="checkbox" checked={key === "showFilterUi" ? config[key] === true : config[key] !== false} onChange={(e) => patch(key, e.target.checked)} />
            <span>{key}</span>
          </label>
        ))}
      </>
    ) : block.type === "search" ? (
      <>
        <div style={ROW}><span style={LABEL}>Heading</span><BlurText value={localeValue("title")} onCommit={(v) => setLocaleValue("title", v)} /></div>
        <div style={ROW}><span style={LABEL}>Placeholder</span><BlurText value={localeValue("placeholder")} onCommit={(v) => setLocaleValue("placeholder", v)} /></div>
        <div style={ROW}><span style={LABEL}>Presentation</span><Dropdown<string> portal size="sm" aria-label={PRESENTATION_LABEL} value={String(config.presentation ?? "")} onChange={(v) => patch("presentation", v || undefined)} options={[{ value: "", label: "hero (default)" }, { value: "hero", label: "hero" }, { value: "compact", label: "compact" }]} /></div>
        <ChipEditor config={config} onPatch={props.onPatchConfig} />
        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer" }}><input type="checkbox" checked={config.chipsOnly === true} onChange={(e) => patch("chipsOnly", e.target.checked)} /><span>Chips only — no free-text box</span></label>
      </>
    ) : block.type === "identity-panel" ? (
      <>
        <div style={GROUP}>Signed out</div>
        <div style={ROW}><span style={LABEL}>Title</span><BlurText value={localeValue("signedOut.title")} onCommit={(v) => setLocaleValue("signedOut.title", v)} /><div style={HINT}>Required.</div>{faultFor("signedOut.title") ? <div style={{ ...WARN, background: "#FBEAE7", borderColor: "#F3D0CB", color: "#C0392B" }}>{faultFor("signedOut.title")}</div> : null}</div>
        <div style={ROW}><span style={LABEL}>Body</span><BlurText value={localeValue("signedOut.body")} lines={3} onCommit={(v) => setLocaleValue("signedOut.body", v)} /><div style={HINT}>Required — and rendered, as of stage 5.</div></div>
        <div style={ROW}><span style={LABEL}>Button</span><BlurText value={localeValue("signedOut.buttonLabel")} onCommit={(v) => setLocaleValue("signedOut.buttonLabel", v)} /></div>
        <div style={GROUP}>Signed in</div>
        <div style={ROW}><span style={LABEL}>Greeting</span><BlurText value={localeValue("signedIn.greetingTemplate")} onCommit={(v) => setLocaleValue("signedIn.greetingTemplate", v)} /><div style={HINT}>{"{name}"} is replaced with the student's first name.</div></div>
        {(["showNextAppointment", "showLiveTicket", "showHoldsSummary", "showHoldAmounts", "showEid"] as const).map((key) => (
          <label key={key} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, cursor: "pointer", marginBottom: 7 }}>
            <input type="checkbox" checked={key === "showEid" ? readPath(config, `signedIn.${key}`) === true : readPath(config, `signedIn.${key}`) !== false} onChange={(e) => patch(`signedIn.${key}`, e.target.checked)} />
            <span>{key}{key === "showEid" ? " — an identifier on a public-ish surface" : ""}</span>
          </label>
        ))}
      </>
    ) : block.type === "help-contact" ? (
      <HelpContactPanel config={config} locale={locale} onPatch={props.onPatchConfig} />
    ) : block.type === "faq" ? (
      <ListPanel
        config={config} locale={locale} onPatch={props.onPatchConfig}
        path="items" addLabel="Add a question"
        fields={[{ path: "question", label: "Question" }, { path: "answer", label: "Answer", lines: 3 }]}
      />
    ) : block.type === "stats" ? (
      <ListPanel
        config={config} locale={locale} onPatch={props.onPatchConfig}
        path="items" addLabel="Add a figure"
        note="These are figures you type, not figures the platform measures. Nothing here is read from live data."
        fields={[{ path: "value", label: "Figure", plain: true }, { path: "label", label: "Label" }]}
      />
    ) : null;

  const dup = (id: string) => props.takenIds.filter((x) => x === id).length > 1;

  return (
    <div style={{ padding: 14, fontFamily: "inherit" }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 2 }}>{osLabel(block.type)}</div>
      <div style={{ ...HINT, marginBottom: 12, ...MONO }}>{block.type} · v{block.version}</div>

      <div style={ROW}>
        <span style={LABEL}>Block id</span>
        <BlurText value={block.id} onCommit={(v) => props.onRename(v)} />
        <div style={HINT}>Named in every publish refusal and audit entry, so keep it readable. Kebab-case, unique on this page.</div>
        {dup(block.id) ? <div style={{ ...WARN, background: "#FBEAE7", borderColor: "#F3D0CB", color: "#C0392B" }}>Another block already has this id — a duplicate refuses the publish.</div> : null}
      </div>

      <div style={ROW}>
        <span style={LABEL}>Who sees it</span>
        <Dropdown<string> portal size="sm" aria-label={VISIBILITY_LABEL} value={block.visibility ?? "always"} onChange={(v) => props.onVisibility(v as any)}
          options={[{ value: "always", label: "Everyone" }, { value: "signed-in", label: "Signed in only" }, { value: "signed-out", label: "Signed out only" }]} />
      </div>

      {props.faults?.some((f) => f.path === null) ? (
        <div style={{ ...WARN, background: "#FBEAE7", borderColor: "#F3D0CB", color: "#C0392B" }}>
          {props.faults.filter((f) => f.path === null).map((f) => f.message).join(" · ")}
        </div>
      ) : null}

      <div style={{ borderTop: "1px solid #F4F5F7", marginTop: 6, paddingTop: 10 }}>
        {hand ?? (generic.length ? generic : <div style={{ fontSize: 11.5, color: "#8A92A0", lineHeight: 1.55 }}>This block has nothing to configure.</div>)}
      </div>
    </div>
  );
}

/** Help links: groups of titled links, each with a target. `linkGroups` is required. */
function HelpContactPanel(props: { config: Record<string, unknown>; locale: string; onPatch: (next: Record<string, unknown>) => void }): JSX.Element {
  const groups = (Array.isArray(props.config.linkGroups) ? props.config.linkGroups : []) as any[];
  const set = (next: any[]) => props.onPatch({ ...props.config, linkGroups: next });
  const text = (value: any): string => (typeof value === "string" ? value : value && typeof value === "object" ? String(Object.values(value)[0] ?? "") : "");
  const L = (v: string) => (v ? { [props.locale]: v } : undefined);
  return (
    <>
      <div style={GROUP}>Link groups</div>
      <div style={HINT}>At least one group is required. A group with no links is valid — the heading still renders.</div>
      {groups.map((group, gi) => (
        <div key={gi} style={{ border: "1px solid #E6E8EC", borderRadius: 8, padding: 9, marginTop: 8 }}>
          <BlurText value={text(group?.title)} placeholder="Group heading" onCommit={(v) => set(groups.map((g, i) => (i === gi ? { ...g, title: L(v) } : g)))} />
          {((group?.links ?? []) as any[]).map((link, li) => (
            <div key={li} style={{ marginTop: 7, paddingLeft: 9, borderLeft: "2px solid #F1EFFC" }}>
              <BlurText value={text(link?.label)} placeholder="Link label" onCommit={(v) => set(groups.map((g, i) => (i === gi ? { ...g, links: (g.links ?? []).map((l: any, j: number) => (j === li ? { ...l, label: L(v) } : l)) } : g)))} />
              <div style={{ marginTop: 5 }}>
                <BlurText
                  value={link?.target?.kind === "deep-link" ? String(link.target.route ?? "") : String(link?.target?.url ?? "")}
                  placeholder="https://… or /in-platform/path"
                  onCommit={(v) => {
                    const target = v.startsWith("/") ? { kind: "deep-link", route: v, params: {} } : v ? { kind: "url", url: v } : undefined;
                    set(groups.map((g, i) => (i === gi ? { ...g, links: (g.links ?? []).map((l: any, j: number) => (j === li ? { ...l, target } : l)) } : g)));
                  }}
                />
                <div style={HINT}>A path beginning with “/” is treated as in-platform; anything else as an external URL.</div>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={() => set(groups.map((g, i) => (i === gi ? { ...g, links: [...(g.links ?? []), {}] } : g)))} style={{ border: "none", background: "transparent", padding: 0, color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Add link</button>
            <button onClick={() => set(groups.filter((_, i) => i !== gi))} style={{ border: "none", background: "transparent", padding: 0, color: "#C0392B", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Remove group</button>
          </div>
        </div>
      ))}
      <button onClick={() => set([...groups, { links: [] }])} style={{ marginTop: 8, height: 28, padding: "0 10px", border: "1px dashed #B9A8F0", borderRadius: 6, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Add a group</button>
    </>
  );
}

/** A repeated pair of fields — faq items, stats figures. */
function ListPanel(props: {
  config: Record<string, unknown>; locale: string; path: string; addLabel: string; note?: string;
  fields: { path: string; label: string; lines?: number; plain?: boolean }[];
  onPatch: (next: Record<string, unknown>) => void;
}): JSX.Element {
  const items = (Array.isArray(props.config[props.path]) ? props.config[props.path] : []) as any[];
  const set = (next: any[]) => props.onPatch({ ...props.config, [props.path]: next });
  const text = (value: any): string => (typeof value === "string" ? value : value && typeof value === "object" ? String(Object.values(value)[0] ?? "") : "");
  return (
    <>
      {props.note ? <div style={HINT}>{props.note}</div> : null}
      {items.map((item, i) => (
        <div key={i} style={{ border: "1px solid #E6E8EC", borderRadius: 8, padding: 9, marginTop: 8 }}>
          {props.fields.map((field) => (
            <div key={field.path} style={{ marginBottom: 6 }}>
              <span style={LABEL}>{field.label}</span>
              <BlurText
                value={field.plain ? String(item?.[field.path] ?? "") : text(item?.[field.path])}
                lines={field.lines}
                onCommit={(v) => set(items.map((row, j) => (j === i ? { ...row, [field.path]: field.plain ? (v || undefined) : (v ? { [props.locale]: v } : undefined) } : row)))}
              />
            </div>
          ))}
          <button onClick={() => set(items.filter((_, j) => j !== i))} style={{ border: "none", background: "transparent", padding: 0, color: "#C0392B", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Remove</button>
        </div>
      ))}
      <button onClick={() => set([...items, {}])} style={{ marginTop: 8, height: 28, padding: "0 10px", border: "1px dashed #B9A8F0", borderRadius: 6, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>{props.addLabel}</button>
    </>
  );
}
